/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable no-console */
/**
 * Persistent storage for Insight Agent chats.
 *
 * Backed by the Spring API (Postgres) so a user's chats follow them across
 * browsers and devices instead of living in one browser's localStorage. The
 * wire shape mirrors {@link StoredChat} — a thin envelope around AI SDK v6
 * `UIMessage[]` plus the page's `displayMap` (RenderBlock[] keyed by
 * `toolCallId`) — so reopening a chat restores its charts faithfully.
 *
 * All calls are scoped server-side to the authenticated user; the sidebar
 * lists lightweight {@link ChatSummary} rows and a full chat is fetched only
 * when it's opened.
 */
import type { UIMessage } from 'ai';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import type { RenderBlock } from './ai-agent.service';

const BASE = `${DEV_NEW_DOMAIN_URL}/ai/chats`;

export interface StoredChat {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: UIMessage[];
  /** Render blocks (charts, cards) keyed by the tool call that produced them. */
  displayMap: Record<string, RenderBlock[]>;
}

/** Lightweight chat metadata used to render the sidebar list. */
export interface ChatSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
}

/**
 * List the current user's saved chats (metadata only), newest-first.
 * Returns an empty list if the request fails so the UI never hard-crashes.
 * @returns Array of chat summaries, newest first.
 */
export async function listChats(): Promise<ChatSummary[]> {
  try {
    const { data } = await axiosInstance.get<ChatSummary[]>(BASE);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[chat-storage] list failed:', err);
    return [];
  }
}

/**
 * Load a single saved chat by id.
 * @param id - Chat id.
 * @returns The stored chat, or undefined if missing / on error.
 */
export async function loadChat(id: string): Promise<StoredChat | undefined> {
  try {
    const { data } = await axiosInstance.get<StoredChat>(`${BASE}/${id}`);
    if (!data) return undefined;
    return {
      ...data,
      messages: Array.isArray(data.messages) ? data.messages : [],
      displayMap: data.displayMap || {}
    };
  } catch (err) {
    console.warn('[chat-storage] load failed:', err);
    return undefined;
  }
}

/**
 * Upsert a chat. Overwrites any existing chat with the same id for this user.
 * @param chat - The chat to persist.
 */
export async function saveChat(chat: StoredChat): Promise<void> {
  try {
    await axiosInstance.put(`${BASE}/${chat.id}`, {
      id: chat.id,
      title: chat.title,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
      messages: chat.messages,
      displayMap: chat.displayMap
    });
  } catch (err) {
    console.warn('[chat-storage] save failed:', err);
  }
}

/**
 * Remove a chat by id.
 * @param id - Chat id.
 */
export async function deleteChat(id: string): Promise<void> {
  try {
    await axiosInstance.delete(`${BASE}/${id}`);
  } catch (err) {
    console.warn('[chat-storage] delete failed:', err);
  }
}

/**
 * Update the title of a chat (fetch-then-save; server has no title-only route).
 * @param id - Chat id.
 * @param title - New title.
 */
export async function renameChat(id: string, title: string): Promise<void> {
  const chat = await loadChat(id);
  if (!chat) return;
  const next = title.trim() || chat.title;
  await saveChat({ ...chat, title: next, updatedAt: Date.now() });
}

/**
 * Generate a new (URL-safe) chat id.
 * @returns A unique chat id string.
 */
export function newChatId(): string {
  return `chat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Derive a short title from the first user message in a chat.
 * @param messages - The chat's UIMessage list.
 * @returns A short title (≤ 60 chars) or a placeholder.
 */
export function deriveChatTitle(messages: UIMessage[]): string {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'Untitled chat';
  const text = (firstUser.parts || [])
    .filter((p) => p.type === 'text')
    .map((p) => (p as { text?: string }).text || '')
    .join(' ')
    .trim();
  if (!text) return 'Untitled chat';
  const words = text.split(/\s+/).slice(0, 8).join(' ');
  return words.length > 60 ? `${words.slice(0, 57)}…` : words;
}

/**
 * Render a unix-ms timestamp as a short relative label ("3m", "2h", "yesterday").
 * @param ts - Unix-ms timestamp.
 * @returns Short relative label.
 */
export function formatRelative(ts: number): string {
  const delta = Math.max(0, Date.now() - ts);
  const sec = Math.floor(delta / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return new Date(ts).toLocaleDateString();
}
